import React from "react";
import { ChevronRight } from "lucide-react";
import { SheetLateral } from "./SheetLateral";
import { RegistroDeConexiones, type ConexionRegistrada } from "./ChipDeIntegracion";

// Las tiendas primero (traen el catálogo), después las redes.
const ORDEN: ConexionRegistrada["clave"][] = ["tiendanube", "mercadolibre", "instagram", "meta"];

/** Para qué sirve cada conexión, en la hoja, mientras no está conectada. */
const BENEFICIO: Record<ConexionRegistrada["clave"], string> = {
  tiendanube: "Traé tu catálogo de productos a Atlas.",
  mercadolibre: "Traé tus publicaciones como productos y subiles las fotos que crees.",
  instagram: "Publicá tus fotos y videos sin salir de Atlas.",
  meta: "Lanzá anuncios en Facebook e Instagram con lo que crees.",
};

/** Lo que dura la salida de `SheetLateral` (`useExitTransition(open, 300)`). */
const SALIDA_DE_LA_HOJA_MS = 300;

/** "A", "A y B", "A, B y C". */
const enumerar = (xs: string[]): string =>
  xs.length <= 1 ? (xs[0] ?? "") : `${xs.slice(0, -1).join(", ")} y ${xs[xs.length - 1]}`;

/**
 * Las conexiones de la marca en mobile: un solo renglón que abre una hoja —
 * ADR-2026-10-05-mi-marca-conexiones-en-un-renglon.
 *
 * Envuelve a los chips de la cabecera (`children`), que en escritorio se ven como
 * siempre y abajo de `lg` no dibujan nada: se anotan en `RegistroDeConexiones` y acá
 * se muestran juntas. El renglón tiene los íconos encimados (con un punto verde o ámbar
 * en las conectadas o vencidas), cuántas hay conectadas y qué falta; la hoja, una fila
 * por conexión con su botón. Los botones llaman a los MISMOS handlers que el chip, así
 * que los modales de conectar y de confirmar la desconexión son los de cada integración.
 */
export const ConexionesDeMarca = ({ children }: { children: React.ReactNode }) => {
  const [registradas, setRegistradas] = React.useState<
    Partial<Record<ConexionRegistrada["clave"], ConexionRegistrada>>
  >({});
  const registrar = React.useCallback((c: ConexionRegistrada) => {
    setRegistradas((prev) => ({ ...prev, [c.clave]: c }));
    return () =>
      setRegistradas((prev) => {
        const siguiente = { ...prev };
        delete siguiente[c.clave];
        return siguiente;
      });
  }, []);
  const conexiones = ORDEN.map((k) => registradas[k]).filter(
    (c): c is ConexionRegistrada => !!c,
  );

  return (
    <RegistroDeConexiones.Provider value={registrar}>
      {conexiones.length > 0 && <Renglon conexiones={conexiones} />}
      {/* `contents` en mobile: los chips no se ven y lo único que queda es el aviso de
          Instagram, que así cae como un renglón más de la cabecera y no deja un hueco
          vacío con su `gap`. */}
      <div className="contents lg:flex lg:flex-wrap lg:items-center lg:gap-2">{children}</div>
    </RegistroDeConexiones.Provider>
  );
};

const Renglon = ({ conexiones }: { conexiones: ConexionRegistrada[] }) => {
  const [hojaAbierta, setHojaAbierta] = React.useState(false);
  const conectadas = conexiones.filter((c) => c.estado === "conectada");
  const vencida = conexiones.find((c) => c.estado === "vencida");
  const pendientes = conexiones.filter((c) => c.estado !== "conectada");
  const total = conexiones.length;

  // Con una sola conexión (Instagram y Meta tienen candado de dev tester) una hoja de
  // un renglón sobra: si falta conectarla, el renglón la conecta directo.
  const unaSolaPendiente = total === 1 && pendientes.length === 1;
  const alTocar = () => (unaSolaPendiente ? conexiones[0]!.onConectar() : setHojaAbierta(true));

  const titulo =
    conectadas.length === 0
      ? total === 1
        ? `Conectá ${conexiones[0]!.nombre}`
        : "Conectá tus canales"
      : conectadas.length === total
        ? total === 1
          ? `${conexiones[0]!.nombre} conectada`
          : "Canales conectados"
        : `${conectadas.length} de ${total} conectadas`;
  const bajada = vencida
    ? `${vencida.nombre} venció · tocá para reconectar`
    : conectadas.length === 0
      ? total === 1
        ? BENEFICIO[conexiones[0]!.clave]
        : enumerar(conexiones.map((c) => c.nombre))
      : pendientes.length > 0
        ? `${enumerar(pendientes.map((c) => c.nombre))} sin conectar`
        : enumerar(conectadas.map((c) => c.detalle || c.nombre));

  // En la hoja, conectar o desconectar cierra la hoja primero y abre el modal de la
  // integración cuando la hoja terminó de irse: `SheetLateral` sigue montado (z-[210])
  // durante su salida y los modales de las integraciones van en z-[80], así que abierto
  // enseguida quedaba tapado — ADR-2026-10-05-mi-marca-la-hoja-se-va-antes-del-modal.
  // Ningún handler abre una ventana de OAuth (eso lo hace el botón del modal), así que
  // demorarlos no choca con el bloqueo de popups de Safari.
  const desdeLaHoja = (accion: () => void) => () => {
    setHojaAbierta(false);
    window.setTimeout(accion, SALIDA_DE_LA_HOJA_MS);
  };

  return (
    <>
      <button
        type="button"
        onClick={alTocar}
        aria-haspopup={unaSolaPendiente ? undefined : "dialog"}
        className="flex w-full items-center gap-3 rounded-2xl border border-black/[0.07] bg-white py-2.5 pl-2.5 pr-3 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-transform active:scale-[0.99] lg:hidden"
      >
        <span className="flex shrink-0 pl-2">
          {conexiones.map((c) => (
            <span
              key={c.clave}
              className="relative -ml-2 flex h-8 w-8 items-center justify-center rounded-full bg-[#F4F5F7] shadow-[0_0_0_2.5px_#fff] [&>svg]:h-4 [&>svg]:w-auto"
            >
              {c.icono}
              {c.estado !== "desconectada" && (
                <span
                  aria-hidden
                  className={`absolute -bottom-px -right-px h-2.5 w-2.5 rounded-full shadow-[0_0_0_2px_#fff] ${
                    c.estado === "vencida" ? "bg-amber-500" : "bg-emerald-500"
                  }`}
                />
              )}
            </span>
          ))}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-semibold text-[#16181D]">{titulo}</span>
          <span
            className={`mt-0.5 block truncate text-[12.5px] ${vencida ? "text-amber-700" : "text-black/50"}`}
          >
            {bajada}
          </span>
        </span>
        {conectadas.length === 0 ? (
          <span className="inline-flex h-7 shrink-0 items-center rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.085)] px-2.5 text-[12.5px] font-semibold text-brand-primary">
            Conectar
          </span>
        ) : (
          <ChevronRight className="h-[18px] w-[18px] shrink-0 text-black/30" />
        )}
      </button>

      <SheetLateral
        open={hojaAbierta}
        onClose={() => setHojaAbierta(false)}
        titulo="Conexiones"
        altoPorContenido
      >
        <p className="-mt-1 mb-2 text-[13.5px] leading-snug text-black/50">
          Conectá tus canales para traer tu catálogo y publicar lo que crees.
        </p>
        <ul className="divide-y divide-black/[0.07]">
          {conexiones.map((c) => (
            <li
              key={c.clave}
              className="flex items-center gap-3 py-3"
            >
              <span className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl bg-[#F4F5F7] [&>svg]:h-5 [&>svg]:w-auto">
                {c.icono}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[14.5px] font-semibold text-[#16181D]">{c.nombre}</span>
                <span
                  className={`mt-0.5 block text-[12.5px] leading-snug ${
                    c.estado === "conectada"
                      ? "truncate text-emerald-700"
                      : c.estado === "vencida"
                        ? "text-amber-700"
                        : "text-black/50"
                  }`}
                >
                  {c.estado === "conectada"
                    ? c.detalle || "Conectada"
                    : c.estado === "vencida"
                      ? "El permiso venció. Volvé a conectar."
                      : BENEFICIO[c.clave]}
                </span>
              </span>
              {c.estado === "conectada" ? (
                <button
                  type="button"
                  onClick={desdeLaHoja(c.onDesconectar)}
                  className="h-8 shrink-0 rounded-full bg-[#F4F5F7] px-3.5 text-[13px] font-semibold text-black/70 transition-colors active:bg-black/[0.08]"
                >
                  Desconectar
                </button>
              ) : (
                <button
                  type="button"
                  onClick={desdeLaHoja(c.onConectar)}
                  className={`h-8 shrink-0 rounded-full px-3.5 text-[13px] font-semibold transition-opacity active:opacity-80 ${
                    c.estado === "vencida"
                      ? "bg-amber-100 text-amber-800"
                      : "bg-brand-primary text-white"
                  }`}
                >
                  {c.estado === "vencida" ? "Reconectar" : "Conectar"}
                </button>
              )}
            </li>
          ))}
        </ul>
      </SheetLateral>
    </>
  );
};
